import { useCallback, useMemo, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import {
  IStudioReviewModel,
  IStudioReviewsAdminModel,
} from "../../../reviews/models/reviewModels";
import {
  IReviewReplyFormModel,
  reviewReplyDefaultValues,
  reviewReplySchema,
} from "../models/reviewReplyFormModel";

const LIST_KEY = [endpointsMetadata.studioReviews.getAll];

/** Dropdown options in the filters panel; no choice means every rating. */
export const RATING_FILTER_OPTIONS = [5, 4, 3, 2, 1].map((stars) => ({
  id: stars,
  name: `${stars} ${stars === 1 ? "звезда" : "звезди"}`,
}));

/** The visibility dropdown takes numbers; these map to the API's isHidden. */
export const VISIBILITY_VISIBLE = 1;
export const VISIBILITY_HIDDEN = 2;
export const VISIBILITY_FILTER_OPTIONS = [
  { id: VISIBILITY_VISIBLE, name: "Видими на сайта" },
  { id: VISIBILITY_HIDDEN, name: "Скрити" },
];

const buildListEndpoint = (rating: number | null, isHidden: boolean | null) => {
  const params = new URLSearchParams();
  if (rating !== null) {
    params.set("rating", String(rating));
  }
  if (isHidden !== null) {
    params.set("isHidden", String(isHidden));
  }
  const query = params.toString();
  return query
    ? `${endpointsMetadata.studioReviews.getAll}?${query}`
    : endpointsMetadata.studioReviews.getAll;
};

/**
 * Administration → "Ревюта" (owner and managers): every review including the hidden
 * ones, filters by stars and visibility (in the filters side panel), hiding a review,
 * a public reply and the settings dialog.
 */
export const useStudioReviewsPage = () => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [ratingFilter, setRatingFilter] = useState<number | null>(null);
  const [hiddenFilter, setHiddenFilter] = useState<boolean | null>(null);
  const [replyTarget, setReplyTarget] = useState<IStudioReviewModel | null>(null);
  const replyForm = useForm<IReviewReplyFormModel>({
    defaultValues: reviewReplyDefaultValues,
    // The schema is typed against the model; the yup resolver widens its fields to optional.
    resolver: yupResolver(reviewReplySchema) as Resolver<IReviewReplyFormModel>,
    mode: "onChange",
  });
  const replyLength = replyForm.watch("reply")?.length ?? 0;

  const { data, isLoading } = useApiQuery<IStudioReviewsAdminModel>({
    queryKey: [
      endpointsMetadata.studioReviews.getAll,
      { rating: ratingFilter, isHidden: hiddenFilter },
    ],
    endpoint: buildListEndpoint(ratingFilter, hiddenFilter),
    // The list shows its own skeleton rows.
    globalIsLoading: false,
  });

  const { mutate, isLoading: isSaving } = useApiMutation();

  const reviews = useMemo(() => data?.items ?? [], [data]);
  const activeFiltersCount =
    (ratingFilter !== null ? 1 : 0) + (hiddenFilter !== null ? 1 : 0);

  const clearFilters = useCallback(() => {
    setRatingFilter(null);
    setHiddenFilter(null);
  }, []);

  const openFilters = useCallback(() => setIsFiltersOpen(true), []);
  const closeFilters = useCallback(() => setIsFiltersOpen(false), []);
  const openSettings = useCallback(() => setIsSettingsOpen(true), []);
  const closeSettings = useCallback(() => setIsSettingsOpen(false), []);

  const toggleHidden = useCallback(
    (review: IStudioReviewModel) => {
      const isHidden = !review.isHidden;
      mutate({
        endpoint: endpointsMetadata.studioReviews.setHidden,
        method: "post",
        payload: { reviewId: review.id, isHidden },
        invalidateKeys: [LIST_KEY],
        successMessage: isHidden
          ? "Ревюто е скрито от сайта"
          : "Ревюто отново се вижда на сайта",
      });
    },
    [mutate],
  );

  const openReply = useCallback(
    (review: IStudioReviewModel) => {
      setReplyTarget(review);
      replyForm.reset({ reply: review.ownerReply ?? "" });
    },
    [replyForm],
  );

  const closeReply = useCallback(() => {
    setReplyTarget(null);
    replyForm.reset(reviewReplyDefaultValues);
  }, [replyForm]);

  const saveReply = useMemo(
    () =>
      replyForm.handleSubmit((values) => {
        if (!replyTarget) {
          return;
        }

        const trimmed = values.reply.trim();
        mutate(
          {
            endpoint: endpointsMetadata.studioReviews.reply,
            method: "post",
            payload: { reviewId: replyTarget.id, reply: trimmed || null },
            invalidateKeys: [LIST_KEY],
            successMessage: "Отговорът е запазен",
          },
          { onSuccess: closeReply },
        );
      }),
    [replyForm, mutate, replyTarget, closeReply],
  );

  return {
    isFiltersOpen,
    openFilters,
    closeFilters,
    isSettingsOpen,
    openSettings,
    closeSettings,
    activeFiltersCount,
    clearFilters,
    reviews,
    isLoading,
    isSaving,
    averageRating: data?.averageRating ?? 0,
    visibleCount: data?.visibleCount ?? 0,
    hiddenCount: data?.hiddenCount ?? 0,
    ratingFilter,
    setRatingFilter,
    hiddenFilter,
    setHiddenFilter,
    toggleHidden,
    replyTarget,
    replyForm,
    replyLength,
    openReply,
    closeReply,
    saveReply,
  };
};
